import { cn, HapticSurface, panelRow, panelRowQuiet, Skeleton } from "@pax/ui";
import { useQuery } from "@tanstack/react-query";
import { Check, Copy, Ticket } from "lucide-react";
import { useState } from "react";
import { inviteLink } from "@/features/waitlist/invites";
import { orpc } from "@/lib/api";
import { Group } from "./parts";

const myInvitesQuery = orpc.waitlist.invites.list.queryOptions();

/** How many of the account's invite links are still unused, once they've loaded. */
export function useInvitesLeft(): number | undefined {
  const { data } = useQuery(myInvitesQuery);
  return data?.items.filter(invite => !invite.redeemedAt).length;
}

/** The account's invite links, each copied with a press, and marked once someone used it. */
export function InvitesSection() {
  const { data } = useQuery(myInvitesQuery);
  const [copied, setCopied] = useState<string | null>(null);
  const left = data?.items.filter(invite => !invite.redeemedAt).length;

  async function copy(code: string) {
    await navigator.clipboard.writeText(inviteLink(code));
    setCopied(code);
    setTimeout(() => setCopied(current => (current === code ? null : current)), 2000);
  }

  return (
    <div className="flex flex-col gap-6">
      <Group
        label={data && data.items.length > 0 ? `${left} of ${data.items.length} left` : undefined}
        note="Each link lets one person past the waitlist."
      >
        {!data ? (
          <div className={panelRowQuiet}>
            <Skeleton className="size-4 shrink-0 rounded" />
            <Skeleton className="h-4 w-28" />
          </div>
        ) : data.items.length === 0 ? (
          <div className={cn(panelRowQuiet, "text-muted-foreground")}>
            <Ticket aria-hidden className="size-4 shrink-0" />
            <span className="min-w-0 flex-1 truncate">No invites to hand out</span>
          </div>
        ) : (
          data.items.map((invite, index) =>
            invite.redeemedAt ? (
              <div key={invite.code} className={cn(panelRowQuiet, "text-muted-foreground")}>
                <Ticket aria-hidden className="size-4 shrink-0" />
                <span className="min-w-0 flex-1 truncate">Invite {index + 1}</span>
                <span className="shrink-0 text-xs">Used</span>
              </div>
            ) : (
              <button
                key={invite.code}
                type="button"
                onClick={() => void copy(invite.code)}
                className={panelRow}
              >
                <Ticket aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate text-left">Invite {index + 1}</span>
                <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                  {copied === invite.code ? (
                    <>
                      <Check aria-hidden className="size-3.5" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy aria-hidden className="size-3.5" />
                      Copy link
                    </>
                  )}
                </span>
                <HapticSurface />
              </button>
            ),
          )
        )}
      </Group>
    </div>
  );
}
